import React, { useState, useEffect, useRef, useMemo } from 'react';
import supportInnovationApi from '../services/support-innovation';
import supportInnovationDocsApi from '../services/support-innovation-documents';
import { DocumentSection } from './shared/DocumentViewer';
import { compressFile } from '../utils/documents';
import { typeInfo, pageName, ticketNum, Icon } from './SupportInnovationShared';

// Textes du parcours, par chemin
const COPY = {
  support: {
    steps: ['Le problème', 'Où ?', 'Détails'],
    q1: 'En une phrase, que se passe-t-il ?',
    ph1: 'Ex. : le bouton « Enregistrer » ne fait rien sur la fiche client',
    q2: 'Où cela se passe-t-il ?',
    q3: 'Racontez-nous ce qui s’est passé',
    hints: ['Ce que vous faisiez', 'Ce qui s’est passé', 'Ce que vous attendiez'],
    ph3: 'J’ouvrais la fiche d’un client, j’ai modifié son téléphone, puis cliqué sur Enregistrer… et rien ne s’est passé.',
    submit: 'Envoyer le signalement',
  },
  evolution: {
    steps: ['L’idée', 'Où ?', 'Détails'],
    q1: 'En une phrase, quelle est votre idée ?',
    ph1: 'Ex. : pouvoir filtrer les leads par concession, ou un outil pour suivre les prêts de véhicules',
    q2: 'Quelle partie d’A97 est concernée ?',
    q3: 'Dites-nous en un peu plus',
    hints: ['Ce que vous aimeriez pouvoir faire', 'Pourquoi ça vous aiderait'],
    ph3: 'Aujourd’hui je dois tout relire à la main. Avec un filtre, je retrouverais mes leads en deux clics.',
    submit: 'Envoyer mon idée',
  },
};

const MIN_TITLE = 8;
const NO_PAGE = '__none__';

export default function SupportInnovationWizard({ type, accessiblePages, parentTicketId = null, onCancel, onCreated, onOpenTicket, onGoMine, onRestart }) {
  const t = typeInfo(type);
  const copy = COPY[t.key];

  const [step, setStep] = useState(1);           // 1 | 2 | 3 | 'done'
  const [titre, setTitre] = useState('');
  const [titleError, setTitleError] = useState('');
  // Garde-fou périmètre : idle | checking | hors_crm | incertain | ok
  const [scope, setScope] = useState({ state: 'idle', raison: '', checkedTitle: '' });
  const [override, setOverride] = useState(false);
  const [page, setPage] = useState('');
  const [pageQuery, setPageQuery] = useState('');
  const [description, setDescription] = useState('');
  const [docs, setDocs] = useState([]);
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [created, setCreated] = useState(null);

  const titleRef = useRef(null);
  const headingRef = useRef(null);

  // À chaque changement d'étape, le focus va au titre de l'étape (lecteurs d'écran)
  useEffect(() => {
    if (step === 1) titleRef.current?.focus();
    else headingRef.current?.focus();
  }, [step]);

  const sortedPages = useMemo(() => {
    const name = (p) => p.nom.slice(p.nom.indexOf(' ') + 1);
    return [...accessiblePages].sort((a, b) => name(a).localeCompare(name(b), 'fr'));
  }, [accessiblePages]);
  const visiblePages = pageQuery.trim()
    ? sortedPages.filter(p => p.nom.toLowerCase().includes(pageQuery.trim().toLowerCase()))
    : sortedPages;

  const onTitleChange = (v) => {
    setTitre(v);
    setTitleError('');
    // Le verdict ne vaut que pour le titre vérifié
    if (scope.state !== 'idle' && v.trim() !== scope.checkedTitle) {
      setScope({ state: 'idle', raison: '', checkedTitle: '' });
      setOverride(false);
    }
  };

  const continueFromTitle = async () => {
    const clean = titre.trim();
    if (clean.length < MIN_TITLE) {
      setTitleError('Ajoutez quelques mots pour qu’on comprenne bien.');
      titleRef.current?.focus();
      return;
    }
    if (scope.checkedTitle === clean && (scope.state === 'ok' || override)) { setStep(2); return; }
    setScope({ state: 'checking', raison: '', checkedTitle: clean });
    try {
      const r = await supportInnovationApi.checkScope(clean, t.key);
      const verdict = r.data?.verdict;
      if (verdict === 'hors_crm' || verdict === 'incertain') {
        setScope({ state: verdict, raison: r.data.raison || '', checkedTitle: clean });
        return;
      }
    } catch {
      // Contrôle indisponible : on ne bloque jamais l'utilisateur
    }
    setScope({ state: 'ok', raison: '', checkedTitle: clean });
    setStep(2);
  };

  const continueAnyway = () => {
    if (scope.state === 'hors_crm') setOverride(true);
    else setScope(s => ({ ...s, state: 'ok' }));
    setStep(2);
  };

  const pickPage = (id) => {
    setPage(id);
    // Petit temps de latence pour voir la sélection avant de passer à la suite
    setTimeout(() => setStep(3), 180);
  };

  // Ctrl+V d'une capture dans la description → ajoutée aux pièces jointes
  const onDescriptionPaste = async (e) => {
    const files = Array.from(e.clipboardData?.items || [])
      .filter(i => i.kind === 'file' && i.type.startsWith('image/'))
      .map(i => i.getAsFile()).filter(Boolean);
    if (!files.length) return;
    e.preventDefault();
    for (const [i, f] of files.entries()) {
      const named = new File([f], `capture-${Date.now()}-${i}.${f.type.split('/')[1] || 'png'}`, { type: f.type });
      const processed = await compressFile(named);
      setDocs(prev => [...prev, {
        id: Date.now() + Math.random(), file: processed, name: processed.name, type: processed.type,
        url: URL.createObjectURL(processed), isLocal: true,
      }]);
    }
  };

  const submit = async () => {
    setSaving(true);
    setSubmitError('');
    try {
      const res = await supportInnovationApi.create({
        type: t.key,
        titre: titre.trim(),
        description: description.trim() || null,
        page: page && page !== NO_PAGE ? page : null,
        scope_override: override,
        ...(parentTicketId ? { parent_ticket_id: parentTicketId } : {}),
      });
      const demande = res.data.demande;
      const failed = [];
      await Promise.all(docs.filter(d => d.isLocal && d.file).map(async (doc) => {
        try {
          if (doc.type === 'application/pdf') {
            const formData = new FormData();
            formData.append('file', doc.file, doc.name);
            await supportInnovationDocsApi.uploadFile(demande.id, formData);
          } else {
            const presign = await supportInnovationDocsApi.presign(demande.id, { filename: doc.name, contentType: doc.type });
            await fetch(presign.data.uploadUrl, { method: 'PUT', body: doc.file, headers: { 'Content-Type': doc.type } });
            await supportInnovationDocsApi.confirm(demande.id, { key: presign.data.key, filename: doc.name, contentType: doc.type, size: doc.file.size });
          }
        } catch (err) {
          console.error('[Support] upload pièce jointe:', doc.name, err);
          failed.push(doc.name);
        }
      }));
      docs.forEach(d => d.url?.startsWith('blob:') && URL.revokeObjectURL(d.url));
      setCreated({ demande, failed });
      setStep('done');
      onCreated && onCreated(demande);
    } catch (err) {
      setSubmitError(err.response?.data?.error || 'L’envoi n’a pas abouti. Vérifiez votre connexion et réessayez.');
    } finally {
      setSaving(false);
    }
  };

  // ── Écran de confirmation ──
  if (step === 'done' && created) {
    return (
      <section className={`sinv-wizard sinv-tone-${t.tone}`} aria-live="polite">
        <div className="sinv-done">
          <div className="sinv-done-mark">{Icon.check(36)}</div>
          <h2 ref={headingRef} tabIndex={-1}>Merci, c’est bien reçu !</h2>
          <p className="sinv-done-text">
            Votre {t.key === 'support' ? 'signalement' : 'idée'} porte le numéro <strong>{ticketNum(created.demande.ticket_number)}</strong>.
            Vous pouvez suivre son avancement à tout moment dans « Mes demandes ».
          </p>
          {created.failed.length > 0 && (
            <p className="sinv-callout sinv-callout--warning" role="alert">
              {created.failed.length} pièce(s) jointe(s) n’ont pas pu être envoyées. Vous pourrez les coller dans les échanges de la demande.
            </p>
          )}
          <div className="sinv-done-actions">
            <button className="sinv-btn sinv-btn--primary" onClick={() => onOpenTicket(created.demande.id)}>
              Voir ma demande {Icon.arrowRight(18)}
            </button>
            <button className="sinv-btn sinv-btn--ghost" onClick={onRestart}>Faire une autre demande</button>
            <button className="sinv-btn sinv-btn--ghost" onClick={onGoMine}>Toutes mes demandes</button>
          </div>
        </div>
      </section>
    );
  }

  const stepIndex = step - 1;
  const blocked = scope.state === 'hors_crm' || scope.state === 'incertain';

  return (
    <section className={`sinv-wizard sinv-tone-${t.tone}`} aria-label={`Nouvelle demande : ${t.label}`}>
      <div className="sinv-wizard-top">
        <button className="sinv-link" onClick={step === 1 ? onCancel : () => setStep(step - 1)}>
          {Icon.arrowLeft(16)} {step === 1 ? 'Retour' : 'Étape précédente'}
        </button>
        <span className="sinv-type-pill">
          {t.key === 'support' ? Icon.bug(16) : Icon.idea(16)} {t.label}
          {parentTicketId && <span className="sinv-type-pill-extra"> · sous-ticket</span>}
        </span>
      </div>

      <ol className="sinv-steps" aria-label="Progression">
        {copy.steps.map((label, i) => (
          <li key={label} className={i < stepIndex ? 'is-done' : i === stepIndex ? 'is-current' : ''} aria-current={i === stepIndex ? 'step' : undefined}>
            <span className="sinv-steps-dot">{i < stepIndex ? Icon.check(14) : i + 1}</span>
            <span className="sinv-steps-label">{label}</span>
          </li>
        ))}
      </ol>

      {/* Étape 1 — le titre, contrôlé dès maintenant (garde-fou matériel / hors CRM) */}
      {step === 1 && (
        <div className="sinv-step" key="s1">
          <label htmlFor="sinv-titre" className="sinv-question">{copy.q1}</label>
          <input
            id="sinv-titre"
            ref={titleRef}
            className={`sinv-title-input${titleError ? ' has-error' : ''}`}
            value={titre}
            onChange={e => onTitleChange(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !blocked && scope.state !== 'checking') continueFromTitle(); }}
            placeholder={copy.ph1}
            maxLength={255}
            autoComplete="off"
            aria-invalid={!!titleError}
            aria-describedby="sinv-titre-help"
          />
          <p id="sinv-titre-help" className={`sinv-help${titleError ? ' is-error' : ''}`}>
            {titleError || 'Pas besoin de détails ici : une phrase simple suffit.'}
          </p>

          {scope.state === 'hors_crm' && (
            <div className="sinv-scope sinv-scope--out" role="alert">
              <div className="sinv-scope-icon">{Icon.monitor(26)}</div>
              <div className="sinv-scope-body">
                <h3>Cela semble être une demande pour le support informatique</h3>
                <p>
                  {scope.raison && <>{scope.raison} </>}
                  Cet espace sert uniquement à faire évoluer <strong>A97</strong> : ses pages, ses boutons, ses données.
                  Pour le matériel (souris, écran, imprimante, ordinateur, téléphone), la connexion ou des logiciels comme Outlook ou Teams,
                  contactez directement votre support informatique : ils vous aideront plus vite.
                </p>
                <div className="sinv-scope-actions">
                  <button className="sinv-btn sinv-btn--soft" onClick={() => titleRef.current?.focus()}>Reformuler mon titre</button>
                  <button className="sinv-link" onClick={continueAnyway}>Non, cela concerne bien A97 {Icon.arrowRight(14)}</button>
                </div>
              </div>
            </div>
          )}

          {scope.state === 'incertain' && (
            <div className="sinv-scope sinv-scope--unsure" role="status">
              <div className="sinv-scope-icon">{Icon.info(26)}</div>
              <div className="sinv-scope-body">
                <h3>Pouvez-vous préciser un peu ?</h3>
                <p>
                  {scope.raison || 'Votre titre est un peu court pour qu’on comprenne de quoi il s’agit.'} Indiquez par exemple la page ou l’action concernée
                  {t.key === 'support' ? ' : « Impossible d’ajouter un véhicule sur la fiche client ».' : ' : « Ajouter un filtre par vendeur dans les Leads ».'}
                </p>
                <div className="sinv-scope-actions">
                  <button className="sinv-btn sinv-btn--soft" onClick={() => titleRef.current?.focus()}>Préciser mon titre</button>
                  <button className="sinv-link" onClick={continueAnyway}>Continuer quand même {Icon.arrowRight(14)}</button>
                </div>
              </div>
            </div>
          )}

          {!blocked && (
            <div className="sinv-step-actions">
              <button className="sinv-btn sinv-btn--primary" onClick={continueFromTitle} disabled={scope.state === 'checking'}>
                {scope.state === 'checking' ? <><span className="sinv-spinner" aria-hidden="true" /> Vérification…</> : <>Continuer {Icon.arrowRight(18)}</>}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Étape 2 — la page concernée, en un clic */}
      {step === 2 && (
        <div className="sinv-step" key="s2">
          <h2 className="sinv-question" ref={headingRef} tabIndex={-1}>{copy.q2}</h2>
          <p className="sinv-help">Choisissez la page. Pas sûr ? Passez simplement à la suite.</p>
          {sortedPages.length > 8 && (
            <div className="sinv-page-search">
              {Icon.search(18)}
              <input value={pageQuery} onChange={e => setPageQuery(e.target.value)} placeholder="Rechercher une page…" aria-label="Rechercher une page" />
            </div>
          )}
          <div className="sinv-page-grid" role="radiogroup" aria-label="Page concernée">
            {visiblePages.map(p => (
              <button
                key={p.id}
                role="radio"
                aria-checked={page === p.id}
                className={`sinv-page-chip${page === p.id ? ' is-selected' : ''}`}
                onClick={() => pickPage(p.id)}
              >
                {p.nom}
              </button>
            ))}
            {visiblePages.length === 0 && <p className="sinv-help">Aucune page ne correspond à « {pageQuery} ».</p>}
          </div>
          <div className="sinv-step-actions">
            <button
              aria-pressed={page === NO_PAGE}
              className={`sinv-btn sinv-btn--ghost${page === NO_PAGE ? ' is-selected' : ''}`}
              onClick={() => pickPage(NO_PAGE)}
            >
              Je ne sais pas / plusieurs pages
            </button>
          </div>
        </div>
      )}

      {/* Étape 3 — description libre + captures, puis envoi */}
      {step === 3 && (
        <div className="sinv-step" key="s3">
          <div className="sinv-recap">
            <div className="sinv-recap-title">« {titre.trim()} »</div>
            <div className="sinv-recap-meta">
              {page && page !== NO_PAGE ? pageName(page) : 'Page non précisée'}
              <button className="sinv-link sinv-link--small" onClick={() => setStep(1)}>{Icon.edit(13)} Modifier</button>
            </div>
          </div>

          <label htmlFor="sinv-desc" className="sinv-question" ref={headingRef} tabIndex={-1}>{copy.q3}</label>
          <ul className="sinv-hints" aria-label="Ce qui nous aide">
            {copy.hints.map(h => <li key={h}>{h}</li>)}
          </ul>
          <textarea
            id="sinv-desc"
            className="sinv-textarea"
            rows={6}
            value={description}
            onChange={e => setDescription(e.target.value)}
            onPaste={onDescriptionPaste}
            placeholder={copy.ph3}
          />
          <p className="sinv-help">Facultatif, mais plus vous êtes précis, plus on va vite.</p>

          <div className="sinv-attach">
            <div className="sinv-attach-head">
              {Icon.image(18)}
              <div>
                <strong>Une capture d’écran ?</strong>
                <span>Appuyez sur <kbd>Impr. écran</kbd> puis <kbd>Ctrl</kbd>+<kbd>V</kbd> dans le texte ci-dessus, ou déposez un fichier ici.</span>
              </div>
            </div>
            <DocumentSection documents={docs} setDocuments={setDocs} parentId={null} docsApi={supportInnovationDocsApi} />
          </div>

          {submitError && <p className="sinv-callout sinv-callout--error" role="alert">{submitError}</p>}

          <div className="sinv-step-actions">
            <button className="sinv-btn sinv-btn--primary sinv-btn--lg" onClick={submit} disabled={saving}>
              {saving ? <><span className="sinv-spinner" aria-hidden="true" /> Envoi…</> : <>{Icon.send(18)} {copy.submit}</>}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
